import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { CHART_COLORS } from "visual-components/analytics/campaign/chartColors";

/**
 * Donut Chart Component using MUI X Charts
 * @param {Object} props
 * @param {Array} props.items - Array of items with x (label) and y (value) properties
 * @param {Object} [props.colorMap={}] - Optional color map for custom colors
 * @param {Object} [props.hideMap={}] - Optional map of items to hide
 * @param {boolean} [props.percentHoverOnly=false] - Show percentage on hover only
 * @returns {import("react").ReactElement}
 */
export default function Donut(props) {
  const { items = [], colorMap = {}, hideMap = {}, percentHoverOnly = false } = props;
  const [highlightedItem, setHighlightedItem] = useState(null);

  // Filter out hidden items and transform data for MUI X PieChart
  const filteredItems = items.filter((item) => {
    const key = item.id ? item.id : item.name || item.x;
    return !hideMap[key];
  });

  /** @type {import("@mui/x-charts").PieValueType[]} */
  const pieData = filteredItems.map((item, index) => {
    const label = item.x || item.name || item.label || `Item ${index}`;
    const value = item.y || item.value || 0;
    const color = colorMap[label] || CHART_COLORS[index % CHART_COLORS.length];

    return {
      id: index,
      value,
      label,
      color,
    };
  });

  const total = pieData.reduce((sum, item) => sum + item.value, 0);

  // Calculate percentage for highlighted item
  const getPercentage = () => {
    if (highlightedItem !== null && pieData[highlightedItem]) {
      const value = pieData[highlightedItem].value;
      return total > 0 ? Math.round((value / total) * 100) : 0;
    }
    return null;
  };

  const percentage = getPercentage();

  return (
    <Box
      sx={{
        width: 126,
        height: 126,
      }}
    >
      <PieChart
        series={[
          {
            data: pieData,
            innerRadius: 32,
            outerRadius: 60,
            paddingAngle: 0,
            cornerRadius: 0,
            highlightScope: { fade: "global", highlight: "item" },
            faded: { additionalRadius: -6, color: "gray" },
            highlighted: { additionalRadius: 6 },
          },
        ]}
        width={126}
        height={126}
        hideLegend
        skipAnimation={false}
        onHighlightChange={(highlighted) => {
          setHighlightedItem(highlighted?.dataIndex ?? null);
        }}
      />
      {/* Center text showing percentage on hover */}
      {percentHoverOnly && percentage !== null && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold", lineHeight: 1 }}>
            {percentage}%
          </Typography>
        </Box>
      )}
    </Box>
  );
}
